import React, { useState, useEffect, useCallback } from 'react';
import CustomNavbar from '../CustomNavbar';
import api from '../axiosSetup';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaBullhorn } from 'react-icons/fa';

const CHANGE_TYPES = [
  { value: 'new',         label: 'Neu' },
  { value: 'fix',         label: 'Fix' },
  { value: 'improvement', label: 'Verbesserung' },
  { value: 'breaking',    label: 'Breaking' },
];

const TYPE_COLORS = {
  new:         '#16a34a',
  fix:         '#2563eb',
  improvement: '#d97706',
  breaking:    '#dc2626',
};

const emptyForm = {
  version: '',
  title: '',
  summary: '',
  changes: [{ type: 'new', text: '' }],
  publish: false,
};

const AdminChangelog = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/changelog');
      setEntries(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setEditId(entry.id);
    setForm({
      version: entry.version,
      title: entry.title,
      summary: entry.summary || '',
      changes: entry.changes && entry.changes.length > 0
        ? entry.changes
        : [{ type: 'new', text: '' }],
      publish: !!entry.is_published,
    });
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditId(null);
    setError('');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addChangeItem = () => {
    setForm(prev => ({ ...prev, changes: [...prev.changes, { type: 'new', text: '' }] }));
  };

  const removeChangeItem = (idx) => {
    setForm(prev => ({ ...prev, changes: prev.changes.filter((_, i) => i !== idx) }));
  };

  const updateChangeItem = (idx, field, value) => {
    setForm(prev => {
      const updated = [...prev.changes];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, changes: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        version: form.version.trim(),
        title: form.title.trim(),
        summary: form.summary.trim() || null,
        changes: form.changes.filter(c => c.text.trim() !== ''),
        publish: form.publish,
      };
      if (editId) {
        await api.put(`/api/admin/changelog/${editId}`, payload);
        setSuccess('Version erfolgreich aktualisiert.');
      } else {
        await api.post('/api/admin/changelog', payload);
        setSuccess('Version erfolgreich erstellt.');
      }
      setShowForm(false);
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    try {
      await api.delete(`/api/admin/changelog/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Löschen.');
    }
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-dark text-white" style={{ minHeight: '100vh' }}>
      <CustomNavbar />
      <div className="container py-4" style={{ maxWidth: 860 }}>

        <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
          <div className="d-flex align-items-center gap-3">
            <div style={{
              width: 44, height: 44, borderRadius: '10px',
              background: 'rgba(250,204,21,0.12)', border: '1.5px solid rgba(250,204,21,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem',
            }}>
              <FaBullhorn style={{ color: '#facc15' }} />
            </div>
            <div>
              <h2 className="mb-0" style={{ fontWeight: 700 }}>Changelog-Verwaltung</h2>
              <p className="mb-0 text-secondary" style={{ fontSize: '0.875rem' }}>Versionen erstellen, bearbeiten und veröffentlichen</p>
            </div>
          </div>
          <button className="btn btn-warning fw-semibold" onClick={openCreate}>
            <FaPlus className="me-2" />Neue Version
          </button>
        </div>

        {success && (
          <div className="alert alert-success d-flex align-items-center gap-2">
            <FaCheck /> {success}
          </div>
        )}

        {showForm && (
          <div style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem',
          }}>
            <h5 className="mb-3">{editId ? 'Version bearbeiten' : 'Neue Version erstellen'}</h5>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label className="form-label text-secondary">Version *</label>
                  <input
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="z. B. 1.4.0"
                    value={form.version}
                    onChange={e => handleChange('version', e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-9">
                  <label className="form-label text-secondary">Titel *</label>
                  <input
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Kurzbeschreibung der Version"
                    value={form.title}
                    onChange={e => handleChange('title', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label text-secondary">Zusammenfassung</label>
                <textarea
                  className="form-control bg-dark text-white border-secondary"
                  rows={2}
                  placeholder="Optionaler Einleitungstext zur Version"
                  value={form.summary}
                  onChange={e => handleChange('summary', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="form-label text-secondary mb-0">Änderungseinträge</label>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addChangeItem}>
                    <FaPlus className="me-1" />Eintrag
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {form.changes.map((item, idx) => (
                    <div key={idx} className="d-flex gap-2 align-items-start">
                      <select
                        className="form-select bg-dark text-white border-secondary"
                        style={{ width: 150, flexShrink: 0 }}
                        value={item.type}
                        onChange={e => updateChangeItem(idx, 'type', e.target.value)}
                      >
                        {CHANGE_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <input
                        className="form-control bg-dark text-white border-secondary"
                        placeholder="Beschreibung der Änderung"
                        value={item.text}
                        onChange={e => updateChangeItem(idx, 'text', e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeChangeItem(idx)}
                        disabled={form.changes.length <= 1}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="publish-check"
                    checked={form.publish}
                    onChange={e => handleChange('publish', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="publish-check">
                    Sofort veröffentlichen
                  </label>
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={cancelForm}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-warning fw-semibold" disabled={saving}>
                    {saving ? 'Speichert…' : editId ? 'Speichern' : 'Erstellen'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {loading && (
          <div className="text-center py-5 text-secondary">
            <div className="spinner-border spinner-border-sm me-2" />Wird geladen…
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center py-5 text-secondary">Noch keine Einträge.</div>
        )}

        {!loading && entries.map(entry => (
          <div
            key={entry.id}
            style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '0.75rem',
              display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <span style={{
                  fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem',
                  color: entry.is_published ? '#facc15' : 'rgba(255,255,255,0.5)',
                }}>
                  v{entry.version}
                </span>
                <span style={{ fontWeight: 600 }}>{entry.title}</span>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 700, padding: '0.1rem 0.5rem',
                  borderRadius: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                  background: entry.is_published ? 'rgba(22,163,74,0.2)' : 'rgba(255,255,255,0.1)',
                  color: entry.is_published ? '#4ade80' : 'rgba(255,255,255,0.4)',
                  border: entry.is_published ? '1px solid rgba(74,222,128,0.3)' : '1px solid rgba(255,255,255,0.1)',
                }}>
                  {entry.is_published ? 'Veröffentlicht' : 'Entwurf'}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>
                {entry.is_published ? `Veröffentlicht: ${formatDate(entry.released_at)}` : `Erstellt: ${formatDate(entry.created_at)}`}
                {entry.changes && entry.changes.length > 0 && ` · ${entry.changes.length} Einträge`}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => openEdit(entry)}
                title="Bearbeiten"
                aria-label="Eintrag bearbeiten"
              >
                <FaEdit />
              </button>
              {!entry.is_published && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(entry.id)}
                  title="Löschen"
                  aria-label="Eintrag löschen"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminChangelog;
